import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { adminSubscribersService, type ImportRow } from '@/services/admin-subscribers.service';
import { ApiError } from '@/services/http';
import type { ImportSummary, SubscriberListView } from '@/types/api';

interface CsvImportDialogProps {
  open: boolean;
  lists: SubscriberListView[];
  onClose: () => void;
  onImported: (summary: ImportSummary) => void;
}

/**
 * Lightweight CSV importer. Parses the file in-browser (header row with
 * email / first_name / last_name / phone, in any order) into rows and POSTs
 * them to the bulk-import endpoint, which upserts + dedupes server-side.
 */
export function CsvImportDialog({ open, lists, onClose, onImported }: CsvImportDialogProps) {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [listId, setListId] = useState<string>('');
  const [overwriteList, setOverwriteList] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setRows([]);
    setFileName('');
    setParseError(null);
    setListId('');
    setOverwriteList(false);
  };

  const handleFile = async (file: File) => {
    setFileName(file.name);
    setParseError(null);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length === 0) {
        setParseError('No data rows found (need a header row with an "email" column).');
        setRows([]);
        return;
      }
      setRows(parsed);
    } catch {
      setParseError('Could not read that file.');
      setRows([]);
    }
  };

  const handleSubmit = async () => {
    if (rows.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const summary = await adminSubscribersService.import({
        rows,
        listId: listId || null,
        overwriteList,
      });
      onImported(summary);
      reset();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-or-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-or-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[18px] font-bold text-or-slate-900">Import subscribers</h2>
        <p className="mt-1 text-[12px] text-or-slate-500">
          CSV with a header row. Recognised columns: <code>email</code>, <code>first_name</code>,{' '}
          <code>last_name</code>, <code>phone</code>. Existing emails are updated, never duplicated.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
            className="text-[13px] text-or-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-or-slate-900 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-white"
          />
          {parseError ? <p className="text-[12px] text-red-600">{parseError}</p> : null}
          {rows.length > 0 ? (
            <p className="text-[12px] font-semibold text-or-slate-700">
              {fileName}: {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'} ready.
            </p>
          ) : null}

          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
            Assign to list (optional)
            <select
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] font-normal normal-case text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
            >
              <option value="">Don't assign</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>

          {listId ? (
            <label className="inline-flex items-center gap-2 text-[12px] text-or-slate-700">
              <input
                type="checkbox"
                checked={overwriteList}
                onChange={(e) => setOverwriteList(e.target.checked)}
                className="h-4 w-4 rounded border-or-slate-300 text-or-orange-600"
              />
              Move subscribers already on another list onto this one
            </label>
          ) : null}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={rows.length === 0 || submitting}
          >
            {submitting ? 'Importing…' : `Import ${rows.length || ''}`.trim()}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Minimal CSV parser: header row maps columns, handles simple quoted fields. */
function parseCsv(text: string): ImportRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) return [];

  const header = splitLine(lines[0] ?? '').map((h) => h.trim().toLowerCase());
  const idx = {
    email: header.findIndex((h) => h === 'email'),
    firstName: header.findIndex((h) => h === 'first_name' || h === 'firstname' || h === 'first'),
    lastName: header.findIndex((h) => h === 'last_name' || h === 'lastname' || h === 'last'),
    phone: header.findIndex((h) => h === 'phone' || h === 'phone_number'),
  };
  if (idx.email < 0) return [];

  const rows: ImportRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitLine(lines[i] ?? '');
    const email = (cells[idx.email] ?? '').trim();
    if (!email) continue;
    rows.push({
      email,
      firstName: idx.firstName >= 0 ? cells[idx.firstName]?.trim() || undefined : undefined,
      lastName: idx.lastName >= 0 ? cells[idx.lastName]?.trim() || undefined : undefined,
      phone: idx.phone >= 0 ? cells[idx.phone]?.trim() || undefined : undefined,
    });
  }
  return rows;
}

function splitLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
